import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Snackbar from '@mui/material/Snackbar';
import Typography from '@mui/material/Typography';
import { useContext, useEffect, useState } from 'react';
import Draggable from 'react-draggable';
import { getOutageMessage } from './_api';
import { UserContext } from 'context/User/UserContext';
import WithLoadingHandler from '../withLoadingHandler/withLoadingHandler';
import { useQuery } from '../../utils/hooks/useQuery';

/**
 * Show outage message for IMS when appropriate:
 *  1. Query parameters do not contain "mode=PPT"
 *  2. Logged in user does not have IMS Dev Team role
 *  3. Site is not IMS Read-Only
 *  4. T_IMS_Outage table has at least one active row
 */
const WithOutageCheck = ({ children }) => {
	const ROLE_DEVTEAM = 18;
	let queryParamMode = useQuery().get('mode');
	const [outageMessage, setOutageMessage] = useState(undefined);
	const [exemption, setExemption] = useState(undefined);
	const [showLoading, setShowLoading] = useState(true);
	const [showOutage, setShowOutage] = useState(false);
	const [showContents, setShowContents] = useState(false);
	/**
	 * @type {import('context/User/UserContext').ContextUser}
	 */
	const contextUser = useContext(UserContext);
	const user = contextUser.userInfo.userProfile;

	useEffect(() => {
		if (user?.ID) {
			if (
				queryParamMode === 'PPT' ||
				process.env.REACT_APP_IMS_ENV?.toLowerCase() === 'readonly' ||
				(user.Roles ?? []).some(r => [ROLE_DEVTEAM].includes(r.ID))
			) {
				setExemption(true);
			} else {
				setExemption(false);
			}

			getOutageMessage()
				.then(response => {
					setOutageMessage(response);
				})
				.catch(() => {
					setOutageMessage(null);
				});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [user]);

	useEffect(() => {
		if (exemption !== undefined && outageMessage !== undefined) {
			if (!!!outageMessage) {
				// no outage message to display: show contents regardless
				setShowContents(true);
				setShowOutage(false);
			} else {
				if (exemption) {
					// outage message to display, but exemption found:show outage as alert
					setShowContents(true);
					setShowOutage(true);
				} else {
					// outage to display and no exemption found: show outage as contents
					setShowContents(false);
					setShowOutage(true);
				}
			}
			//all data set, disable loading
			setShowLoading(false);
		} else {
			//waiting on data to be set, show loading
			setShowLoading(true);
		}
	}, [exemption, outageMessage]);

	const outageDisplay = (
		<Box sx={{ display: 'flex', justifyContent: 'space-around' }}>
			<Card
				sx={{
					width: '50%',
					bgcolor: 'lightgoldenrodyellow',
					padding: '1rem',
					margin: '2rem',
				}}
				variant='outlined'
			>
				<CardContent>
					<Typography
						gutterBottom
						variant='h4'
						component='div'
						color='red'
						fontWeight='bold'
					>
						Outage:
					</Typography>
					<Typography variant='h6' color='black' fontWeight='bold'>
						{outageMessage}
					</Typography>
				</CardContent>
			</Card>
		</Box>
	);

	return (
		<WithLoadingHandler isLoading={showLoading} showChildren={!showLoading}>
			{showContents ? (
				<>
					{showOutage && (
						<Draggable>
							<Snackbar
								open={true}
								anchorOrigin={{
									vertical: 'bottom',
									horizontal: 'center',
								}}
							>
								<Alert
									severity={'warning'}
									variant={'filled'}
									style={{ cursor: 'move' }}
								>
									<AlertTitle>
										<span id={'outage-title'}>
											You are viewing IMS during outage
											via exemption
										</span>
									</AlertTitle>
									<em id={'outage-message'}>
										{outageMessage
											?.split('<BR>')
											.map((line, index) => (
												<>
													{index > 0 && <br />}
													{line}
												</>
											))}
									</em>
								</Alert>
							</Snackbar>
						</Draggable>
					)}
					{children}
				</>
			) : (
				outageDisplay
			)}
		</WithLoadingHandler>
	);
};

export default WithOutageCheck;
